import { ChangeDetectionStrategy, Component, Injector, Input } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {
    EditPanelV2Component,
    OnFormDataLoad
} from '@Work/Accounts/proprietary/controls/form-controls/edit-panel/edit-panel-v2.component';
import { PageObjectService } from '@Core/models/proprietary/page/page-object.service';
import { ProjectObjectService } from '@Core/models/proprietary/project/project-object.service';
import { ProjectObjectState } from '@Core/models/proprietary/project/project-object.state';
import { TaskObjectService } from '@Core/models/proprietary/task/task-object.service';
import { TaskObjectState } from '@Core/models/proprietary/task/task-object.state';
import { EventType, TaskActionType } from '@Core/models/proprietary/task/task-object.types';
import { ObjectService } from '@Core/services/proprietary/object.service';

@Component({
    standalone: false,
    selector: 'ep-task-action-edit-panel',
    templateUrl: './task-action-edit-panel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskActionEditPanelComponent extends EditPanelV2Component implements OnFormDataLoad {
    ignorePageState = true; // overrides automatic readonly, error display mode from page state
    readOnlyMode = false; //override readonly mode

    _taskActionMode: TaskActionType = null;
    _taskObjectCode: string;
    _routeToValues: any = [];
    _autoAssistRoutingOptions: any = [];

    _quickSelectDateCodes = [
        { Code: 'T', Code_Desc: 'today' },
        { Code: 'D1', Code_Desc: 'today + 1 day' },
        { Code: 'D2', Code_Desc: 'today + 2 days' },
        { Code: 'D3', Code_Desc: 'today + 3 days' },
        { Code: 'D4', Code_Desc: 'today + 4 days' },
        { Code: 'D5', Code_Desc: 'today + 5 days' },
        { Code: 'W1', Code_Desc: 'today + 1 week' },
        { Code: 'W2', Code_Desc: 'today + 2 weeks' },
        { Code: 'W3', Code_Desc: 'today + 3 weeks' },
        { Code: 'W4', Code_Desc: 'today + 4 weeks' },
        { Code: 'M1', Code_Desc: 'today + 1 month' },
        { Code: 'M2', Code_Desc: 'today + 2 months' },
        { Code: 'M3', Code_Desc: 'today + 3 months' },
        { Code: 'M6', Code_Desc: 'today + 6 months' },
        { Code: 'M9', Code_Desc: 'today + 9 months' },
        { Code: 'Y1', Code_Desc: 'today + 1 year' }
    ];

    @Input()
    set taskActionMode(mode: TaskActionType) {
        this._taskActionMode = mode;

        if (mode == TaskActionType.MarkDone) {
            this.pageOS.setValidEvents([EventType.Create, EventType.Complete], EventType.Create, EventType.Complete);
        } else {
            this.pageOS.setValidEvents([EventType.Create, EventType.Complete], EventType.Create, EventType.Create);
        }
    }

    get taskActionMode(): TaskActionType {
        return this._taskActionMode;
    }

    @Input()
    taskActionOptions: any = {};

    constructor(
        public taskOS: TaskObjectService,
        public projectOS: ProjectObjectService,
        private injector: Injector,
        public pageOS: PageObjectService,
        public os: ObjectService
    ) {
        super(injector);

        this.config = {
            objectService: this.taskOS,
            formMapping: [
                {
                    formGroupName: 'Task',
                    nodeType: 'Task',
                    selector: TaskObjectState.task,
                    updateMethod: (data: any) => {
                        this.taskOS.updateTask(data);
                    }
                },
                {
                    formGroupName: 'Project',
                    nodeType: 'Project',
                    selector: ProjectObjectState.project,
                    updateMethod: (data: any) => {
                        this.projectOS.updateProject(data);
                    }
                }
            ]
        };

        this.form = new UntypedFormGroup(
            {
                Task: this.fb.group({
                    Task_ID: [''],
                    Edit_Status: [''],
                    Task_Status: [''],
                    Task_Desc: [''],
                    Task_Note: [''],
                    Task_Object: this.fb.group({
                        Object_Code: [''],
                        Object_ID: [''],
                        Object_Name: ['']
                    }),
                    Assigned_To: this.fb.group({
                        Entity_ID: [''],
                        Entity_Name: [''],
                        WorkGroup_ID: [''],
                        WorkGroup_Name: [''],
                        Date: ['']
                    }),
                    Created_By: this.fb.group({
                        Entity_ID: [''],
                        Entity_Name: [''],
                        Date: ['']
                    }),
                    Started_By: this.fb.group({
                        Entity_ID: [''],
                        Entity_Name: [''],
                        Date: ['']
                    }),
                    Completed_By: this.fb.group({
                        Entity_ID: [''],
                        Entity_Name: [''],
                        Date: ['']
                    }),
                    Active_Date: [''],
                    Active_Date_Quick_Select: [''],
                    Late_Date: [''],
                    Late_Date_Quick_Select: [],
                    Protect_Late_Ind: [''],
                    Saved_Protect_Late_Ind: [''], //note: this is not part of SDK -- needed for UI processing
                    Priority: [''],
                    Category: [''],
                    Note_Required_Ind: [''],
                    Task_Required_Ind: [''],
                    Unattend_Create_Ind: ['N'],
                    Unattend_Post_Ind: ['N'],
                    Unattend_Send_Ind: ['N'],
                    Unattend_Complete_Ind: ['N'],
                    Route: this.fb.group({
                        Assign_To: this.fb.group({
                            Assign_To_Type: ['USER'], //note: this is not part of SDK -- needed for UI processing
                            User_Entity_ID: [''],
                            User_Entity_Name: [''], //note: this is not part of SDK -- needed for UI processing
                            WorkGroup_ID: [''],
                            WorkGroup_Name: [''] //note: this is not part of SDK -- needed for UI processing
                        }),
                        Assign_Note: [''],
                        Notify_Immediate_Ind: ['N'],
                        Notify_Complete_Ind: ['N'],
                        Notify_Late_Ind: ['N'],
                        Notify_Include_Document_Ind: ['N']
                    })
                }),
                Project: this.fb.group({
                    Project_ID: [''],
                    Project_Desc: [''],
                    Project_Owner: this.fb.group({
                        Entity_ID: [''],
                        Entity_Name: ['']
                    })
                })
            },
            { updateOn: 'blur' }
        );
    }

    onFormDataLoad() {
        if (this.taskActionMode == TaskActionType.Route) {
            this.setRouteToValues();
        }
    }

    setRouteToValues() {
        let objectCode = this.form.getRawValue().Task.Task_Object.Object_Code;
        if (objectCode && objectCode != '' && objectCode != this._taskObjectCode) {
            this._taskObjectCode = objectCode;
            this._routeToValues = [
                { Code: 'USER', Code_Desc: 'User', Tooltip: 'Route to a User' },
                { Code: 'WORKGROUP', Code_Desc: 'Workgroup', Tooltip: 'Route to a WorkGroup' }
            ];

            let objectConfig: any = this.os.getObjectConfig(objectCode);
            if (objectConfig && objectConfig.AutoAssist_Ind == 'Y') {
                this._routeToValues.push({
                    Code: 'AUTOASSIST',
                    Code_Desc: 'Automated Assistant',
                    Tooltip: 'Route to Automated Assistant'
                });

                /* Auto create is not needed because task will always be created at point of routing.
                   We will uncomment this when we implement the change for creating a manual task
                   and defining an object type on manual task/*
                /*if (objectConfig.Unattend_Create_Ind === 'Y') {
                  this._autoAssistRoutingOptions.push({
                    Label: 'Auto Create',
                    ControlPath: 'Task.Unattend_Create_Ind'
                  });
                }*/
                if (objectConfig.Unattend_Send_Ind === 'Y') {
                    this._autoAssistRoutingOptions.push({
                        Label: 'Auto Send/Distribute',
                        ControlPath: 'Task.Unattend_Send_Ind'
                    });
                }
                if (objectConfig.Unattend_Post_Ind === 'Y') {
                    this._autoAssistRoutingOptions.push({
                        Label: 'Auto Post/Process',
                        ControlPath: 'Task.Unattend_Post_Ind'
                    });
                }
                if (objectConfig.Unattend_Complete_Ind === 'Y') {
                    this._autoAssistRoutingOptions.push({
                        Label: 'Auto Mark Task Complete',
                        ControlPath: 'Task.Unattend_Complete_Ind'
                    });
                }
            }
        }
    }
}
